import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Input } from '@heroui/react'
import { cardOf, parsePath, problemText, type CardRef } from '../../lib/profilesProblems'
import { validateOperationDoc } from '../../lib/profilesValidate'
import { nameIn } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, ProfileQueue } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Notice } from '../settingsKit'
import type { PickOption } from '../setupKit'

// Pieces the group, segment and queue sheets share.

/** Codes the client mirror cannot check; the last save's are kept on screen. */
export const SERVER_ONLY = new Set(['SERVICE_UNKNOWN', 'PRIMARY_UNUSED'])

/** The field a problem sits under, relative to its card: `name`, `serviceIds`, … ('' for the card itself). */
export function fieldKey(path: string): string {
  const steps = parsePath(path)
  const skip = steps[0]?.key === 'groups' && steps[1]?.key === 'segments' && steps[1].index !== undefined ? 2 : 1
  return steps[skip]?.key ?? ''
}

export type Pinned = ProfileProblem & { card: CardRef }

/** Every problem of the trial document (the sheet's copy put back), with the card it lands on. */
export function useLiveProblems(trial: OperationDoc): Pinned[] {
  return useMemo(() => {
    const { problems, warnings } = validateOperationDoc(trial)
    return [...problems, ...warnings].map((p) => ({ ...p, card: cardOf(p.path, trial) }))
  }, [trial])
}

/** One card's problems: the live mirror's, plus the last save's server-only ones. */
export function cardProblems(live: Pinned[], kind: CardRef['kind'], id: string, saved: ProfileProblem[]): ProfileProblem[] {
  const mine: ProfileProblem[] = live.filter((p) => p.card.kind === kind && p.card.id === id)
  const kept = saved.filter((p) => SERVER_ONLY.has(p.code) && !mine.some((m) => m.code === p.code && m.path === p.path))
  return [...mine, ...kept]
}

/** The problems at the top (no field of their own on this sheet) and a lookup for the rest. */
export function useProblemSlots(problems: ProfileProblem[], placed: readonly string[]) {
  const { lang } = useStaffText()
  const top = problems.filter((p) => !placed.includes(fieldKey(p.path)))
  const head: ReactNode =
    top.length > 0 ? (
      <Notice tone={top.every((p) => p.warning) ? 'warning' : 'danger'}>
        {top.map((p, i) => (
          <span key={i} className="pf-line">
            {problemText(p, lang)}
          </span>
        ))}
      </Notice>
    ) : null
  const at = (field: string): ReactNode => {
    const mine = problems.filter((p) => fieldKey(p.path) === field)
    return mine.length > 0 ? (
      <div className="field-error" role="alert">
        {mine.map((p, i) => (
          <div key={i}>{problemText(p, lang)}</div>
        ))}
      </div>
    ) : null
  }
  const has = (field: string) => problems.some((p) => !p.warning && fieldKey(p.path) === field)
  return { head, at, has }
}

/** Queues in the profile's order, as Pick options (the letter as the note). */
export function queueOptions(queues: readonly ProfileQueue[], lang: Parameters<typeof nameIn>[1]): PickOption[] {
  return [...queues]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((q) => ({ value: q.id, label: nameIn(q.name, lang, q.code), note: q.letter }))
}

// The sheets' on/off chips and segmented choices are setupKit's HeroUI toggles.
export { Segmented, Toggles as Chips } from '../setupKit'

/** A whole-number box that keeps what is typed; empty is `undefined`. */
export function NumInput({
  value,
  onChange,
  min,
  max,
  invalid,
  id,
}: {
  value: number | undefined
  onChange: (n: number | undefined) => void
  min?: number
  max?: number
  invalid?: boolean
  id?: string
}) {
  const shown = (n: number | undefined) => (n === undefined || !Number.isFinite(n) ? '' : String(n))
  const parse = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? undefined : Number(s))
  const [text, setText] = useState(shown(value))
  useEffect(() => {
    if (parse(text) !== value) setText(shown(value))
    // Only an outside change of the value rewrites the box.
  }, [value])
  return (
    <Input
      id={id}
      className="pf-num"
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={text}
      aria-invalid={invalid || undefined}
      onChange={(e) => {
        setText(e.target.value)
        onChange(parse(e.target.value))
      }}
    />
  )
}
